import React, { useState, useEffect, useCallback } from 'react';
import GroupBookingModal from './GroupBookingModal';
import DesinscriptionsModal from './DesinscriptionsModal';

const API_BASE = 'https://surabaya-api.onrender.com/api';

// Flag sécheresse + horaires de départ calèche actifs : voir src/config.js
const CONFIG = require('./config');

const GROUP_STATUS_LABEL = { option: 'Option', confirme: 'Confirmé', annule: 'Annulé' };

// ── Couleurs Surabaya ──────────────────────────────────────────────
const COLORS = {
  brun: '#4A2C17',
  brunLight: '#6B3F24',
  or: '#E8C47C',
  orLight: '#F2D89A',
  cream: '#FDF6EC',
  gris: '#8C7B6E',
  grisClair: '#F5EDE3',
  blanc: '#FFFFFF',
  rouge: '#C0392B',
  vert: '#27AE60',
  bleu: '#2980B9',
};

const TYPE_CONFIG = {
  restaurant: { label: 'Restaurant', color: '#8B5E3C', bg: '#F5EDE3', icon: '🍽️' },
  caleche: { label: 'Calèche', color: '#5D4E37', bg: '#EDE0CC', icon: '🐴' },
  caleche_bateau: { label: 'Calèche + Bateau', color: '#2C6E8A', bg: '#D6EAF2', icon: '🐴⛵' },
  journee_complete: { label: 'Journée complète', color: '#4A2C17', bg: '#E8C47C33', icon: '🌟' },
  caleche_fondue: { label: 'Calèche + Fondue', color: '#7B3F00', bg: '#FAE5D3', icon: '🫕' },
};

const HEURES_CALECHE = ['10:00','13:45','14:30','15:15'];

const HEURES = [
  '11:30','12:00','12:30','13:00','13:30','14:00',
  '18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30',
];

const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

// ── Utilitaires ───────────────────────────────────────────────────
function formatDate(d) {
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateFR(d) {
  const date = new Date(String(d).split('T')[0] + 'T00:00:00');
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  let d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Lundi = 0
}

// ── Styles ────────────────────────────────────────────────────────
const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    backgroundColor: COLORS.cream,
    minHeight: '100vh',
    padding: '0',
    color: COLORS.brun,
  },
  header: {
    backgroundColor: COLORS.brun,
    color: COLORS.or,
    padding: '20px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 4px 12px rgba(74,44,23,0.3)',
  },
  headerTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    letterSpacing: '1px',
    margin: 0,
  },
  headerSub: {
    fontSize: '13px',
    color: COLORS.orLight,
    marginTop: '2px',
    fontStyle: 'italic',
  },
  btnPrimary: {
    backgroundColor: COLORS.or,
    color: COLORS.brun,
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: COLORS.brun,
    border: `2px solid ${COLORS.brun}`,
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  btnDanger: {
    backgroundColor: COLORS.rouge,
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  btnSuccess: {
    backgroundColor: COLORS.vert,
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    fontFamily: 'inherit',
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 32px',
    backgroundColor: COLORS.blanc,
    borderBottom: `1px solid ${COLORS.grisClair}`,
  },
  monthTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: COLORS.brun,
    letterSpacing: '0.5px',
  },
  navBtn: {
    backgroundColor: COLORS.grisClair,
    border: `1px solid ${COLORS.or}`,
    color: COLORS.brun,
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  calendarGrid: {
    padding: '24px 32px',
  },
  calendarHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginBottom: '8px',
  },
  calendarHeaderCell: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: COLORS.gris,
    padding: '8px 0',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  calendarBody: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
  dayCell: {
    minHeight: '90px',
    backgroundColor: COLORS.blanc,
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
    overflow: 'hidden',
  },
  dayCellEmpty: {
    minHeight: '90px',
    backgroundColor: 'transparent',
  },
  dayCellToday: {
    border: `2px solid ${COLORS.or}`,
    backgroundColor: '#FFFBF3',
  },
  dayNumber: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: COLORS.brun,
    marginBottom: '4px',
  },
  dayNumberToday: {
    color: COLORS.blanc,
    backgroundColor: COLORS.brun,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    marginBottom: '4px',
  },
  reservaBadge: {
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '10px',
    marginBottom: '2px',
    fontWeight: '600',
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  moreBadge: {
    fontSize: '10px',
    color: COLORS.gris,
    fontStyle: 'italic',
    marginTop: '2px',
  },
  // Vue journalière
  dayView: {
    padding: '24px 32px',
  },
  dayViewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dayViewTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: COLORS.brun,
    textTransform: 'capitalize',
  },
  statsBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  statCard: {
    backgroundColor: COLORS.blanc,
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '10px',
    padding: '12px 20px',
    textAlign: 'center',
    minWidth: '100px',
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: COLORS.brun,
  },
  statLabel: {
    fontSize: '11px',
    color: COLORS.gris,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  reservaCard: {
    backgroundColor: COLORS.blanc,
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '10px',
    padding: '16px 20px',
    marginBottom: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  reservaTime: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: COLORS.brun,
    minWidth: '60px',
  },
  reservaInfo: {
    flex: 1,
  },
  reservaName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: COLORS.brun,
    marginBottom: '2px',
  },
  reservaDetails: {
    fontSize: '13px',
    color: COLORS.gris,
  },
  typePill: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 'bold',
    letterSpacing: '0.3px',
  },
  emptyDay: {
    textAlign: 'center',
    padding: '60px 20px',
    color: COLORS.gris,
    fontStyle: 'italic',
    fontSize: '16px',
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(74,44,23,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalBox: {
    backgroundColor: COLORS.blanc,
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '540px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(74,44,23,0.4)',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: COLORS.brun,
    marginBottom: '24px',
    paddingBottom: '12px',
    borderBottom: `2px solid ${COLORS.or}`,
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 'bold',
    color: COLORS.gris,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: COLORS.brun,
    backgroundColor: COLORS.cream,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border 0.2s',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: COLORS.brun,
    backgroundColor: COLORS.cream,
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${COLORS.grisClair}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: COLORS.brun,
    backgroundColor: COLORS.cream,
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '80px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  detailRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'flex-start',
  },
  detailIcon: {
    fontSize: '18px',
    minWidth: '24px',
    textAlign: 'center',
    marginTop: '1px',
  },
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: COLORS.gris,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '2px',
  },
  detailValue: {
    fontSize: '15px',
    color: COLORS.brun,
  },
  noteBox: {
    backgroundColor: COLORS.grisClair,
    border: `1px solid ${COLORS.or}`,
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    color: COLORS.brunLight,
    fontStyle: 'italic',
    marginTop: '8px',
  },
  toast: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    backgroundColor: COLORS.brun,
    color: COLORS.or,
    padding: '14px 24px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    boxShadow: '0 8px 24px rgba(74,44,23,0.4)',
    zIndex: 2000,
    animation: 'slideIn 0.3s ease',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: COLORS.gris,
    fontSize: '16px',
  },
  errorBox: {
    backgroundColor: '#FFF0F0',
    border: '1px solid #FFCCCC',
    borderRadius: '8px',
    padding: '12px 16px',
    color: COLORS.rouge,
    fontSize: '14px',
    marginBottom: '16px',
  },
};

// ── Composant Toast ───────────────────────────────────────────────
function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div style={styles.toast}>{message}</div>;
}

// ── Modal Détail Réservation ──────────────────────────────────────
function ReservationDetailModal({ reservation, onClose, onDelete, onEdit, onConfirm }) {
  const type = TYPE_CONFIG[reservation.type] || TYPE_CONFIG.restaurant;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isConfirmed = reservation.statut === 'confirmee';

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <span style={{ ...styles.typePill, backgroundColor: type.bg, color: type.color, display: 'inline-block', marginBottom: '8px' }}>
              {type.icon} {type.label}
            </span>
            {isConfirmed && (
              <span style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#D5F5E3', color: COLORS.vert, padding: '3px 10px', borderRadius: '20px', fontWeight: 'bold' }}>
                ✅ Confirmée
              </span>
            )}
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.brun }}>{reservation.nom}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: COLORS.gris }}>✕</button>
        </div>

        <div style={styles.detailRow}>
          <div style={styles.detailIcon}>📅</div>
          <div style={styles.detailContent}>
            <div style={styles.detailLabel}>Date</div>
            <div style={styles.detailValue}>{formatDateFR(reservation.date)}</div>
          </div>
        </div>

        <div style={styles.detailRow}>
          <div style={styles.detailIcon}>🕐</div>
          <div style={styles.detailContent}>
            <div style={styles.detailLabel}>Heure</div>
            <div style={styles.detailValue}>{reservation.heure}</div>
          </div>
        </div>

        <div style={styles.detailRow}>
          <div style={styles.detailIcon}>👥</div>
          <div style={styles.detailContent}>
            <div style={styles.detailLabel}>Couverts</div>
            <div style={styles.detailValue}>{reservation.nb_personnes} personne{reservation.nb_personnes > 1 ? 's' : ''}</div>
          </div>
        </div>

        {reservation.telephone && (
          <div style={styles.detailRow}>
            <div style={styles.detailIcon}>📞</div>
            <div style={styles.detailContent}>
              <div style={styles.detailLabel}>Téléphone</div>
              <div style={styles.detailValue}>
                <a href={`tel:${reservation.telephone}`} style={{ color: COLORS.brun }}>{reservation.telephone}</a>
              </div>
            </div>
          </div>
        )}

        {reservation.email && (
          <div style={styles.detailRow}>
            <div style={styles.detailIcon}>✉️</div>
            <div style={styles.detailContent}>
              <div style={styles.detailLabel}>Email</div>
              <div style={styles.detailValue}>{reservation.email}</div>
            </div>
          </div>
        )}

        {(reservation.type === 'caleche' || reservation.type === 'journee_complete' || reservation.type === 'caleche_fondue') && (
          <div style={styles.detailRow}>
            <div style={styles.detailIcon}>🐴</div>
            <div style={styles.detailContent}>
              <div style={styles.detailLabel}>Calèche</div>
              <div style={styles.detailValue}>{reservation.nb_caleches || 1} calèche{(reservation.nb_caleches || 1) > 1 ? 's' : ''}</div>
            </div>
          </div>
        )}

        {reservation.commentaires && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: '6px' }}>
            <div style={{ fontSize: '11px', color: '#78350f', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>💬 Commentaires</div>
            <div style={{ fontSize: '14px', color: '#78350f', whiteSpace: 'pre-wrap' }}>{reservation.commentaires}</div>
          </div>
        )}

        {reservation.source === 'admin' && (
          <div style={{ marginTop: '12px' }}>
            <span style={{ fontSize: '11px', color: COLORS.gris, backgroundColor: COLORS.grisClair, padding: '3px 10px', borderRadius: '20px' }}>
              📋 Saisie manuelle
            </span>
          </div>
        )}

        <div style={styles.modalActions}>
          {!confirmDelete ? (
            <>
              {!isConfirmed && (
                <button style={styles.btnSuccess} onClick={() => onConfirm(reservation.id)}>
                  ✅ Confirmer
                </button>
              )}
              <button style={styles.btnSecondary} onClick={() => onEdit(reservation)}>✏️ Modifier</button>
              <button style={styles.btnDanger} onClick={() => setConfirmDelete(true)}>🗑️ Annuler la résa</button>
              <button style={styles.btnPrimary} onClick={onClose}>Fermer</button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '14px', color: COLORS.rouge, alignSelf: 'center' }}>Confirmer l'annulation ?</span>
              <button style={styles.btnSecondary} onClick={() => setConfirmDelete(false)}>Non</button>
              <button style={styles.btnDanger} onClick={() => onDelete(reservation.id)}>Oui, annuler</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal Formulaire Réservation ──────────────────────────────────
function ReservationFormModal({ onClose, onSave, initialData, initialDate }) {
  const today = formatDate(new Date());
  const [form, setForm] = useState({
    nom: '',
    telephone: '',
    email: '',
    type: 'restaurant',
    date: initialDate || today,
    heure: '12:00',
    nb_personnes: 2,
    nb_caleches: 1,
    commentaires: '',
    ...initialData,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!initialData?.id;
  const showCaleche = ['caleche','caleche_bateau','journee_complete'].includes(form.type);
  const isCaleche = ['caleche','caleche_bateau','journee_complete'].includes(form.type);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nom.trim()) return setError('Le nom est obligatoire');
    if (!form.date) return setError('La date est obligatoire');
    if (!form.heure) return setError("L'heure est obligatoire");
    if (!form.nb_personnes || form.nb_personnes < 1) return setError('Le nombre de personnes est obligatoire');

    setLoading(true);
    setError('');
    try {
      // canal 'admin' à la création : bypass fermetures + limite couverts côté backend
      await onSave({ ...form, source: 'admin', ...(isEdit ? {} : { canal: 'admin' }) });
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={styles.modalTitle}>
          {isEdit ? '✏️ Modifier la réservation' : '➕ Nouvelle réservation'}
        </div>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nom *</label>
            <input style={styles.input} value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Nom du client" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Téléphone</label>
            <input style={styles.input} value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Email</label>
          <input style={styles.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@email.com" />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Type de réservation *</label>
          <select style={styles.select} value={form.type} onChange={e => {
            const t = e.target.value;
            const isCal = ['caleche','caleche_bateau','journee_complete','caleche_fondue'].includes(t);
            const defaultHeure = t === 'journee_complete' ? '11:30' : t === 'caleche_fondue' ? '10:00' : isCal ? '10:00' : '12:00';
            setForm(f => ({ ...f, type: t, heure: defaultHeure }));
          }}>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.label}</option>
            ))}
          </select>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date *</label>
            <input style={styles.input} type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Heure *</label>
            {form.type === 'caleche_fondue'
              ? <input style={styles.input} type="text" value={form.heure} onChange={e => set('heure', e.target.value)} placeholder="ex: 19:30" maxLength="5" />
              : <select style={styles.select} value={form.heure} onChange={e => set('heure', e.target.value)}>
                  {(form.type === 'journee_complete' ? ['11:30'] : isCaleche ? HEURES_CALECHE : HEURES).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
            }
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nombre de personnes *</label>
            <input style={styles.input} type="number" min="1" max="100" value={form.nb_personnes}
              onChange={e => set('nb_personnes', e.target.value === '' ? '' : parseInt(e.target.value) || 1)}
              onBlur={e => { if (!e.target.value || e.target.value < 1) set('nb_personnes', 1); }} />
          </div>
          {showCaleche && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Nombre de calèches</label>
              <input style={styles.input} type="number" min="1" max="5" value={form.nb_caleches}
                onChange={e => set('nb_caleches', e.target.value === '' ? '' : parseInt(e.target.value) || 1)}
                onBlur={e => { if (!e.target.value || e.target.value < 1) set('nb_caleches', 1); }} />
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Particularités / Notes</label>
          <textarea style={styles.textarea} value={form.commentaires}
            onChange={e => set('commentaires', e.target.value)}
            placeholder="Allergies, anniversaire, demandes spéciales..." />
        </div>

        <div style={styles.modalActions}>
          <button style={styles.btnSecondary} onClick={onClose}>Annuler</button>
          <button style={{ ...styles.btnPrimary, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Enregistrement...' : (isEdit ? '✅ Enregistrer' : '✅ Créer la réservation')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Composant Principal ───────────────────────────────────────────
export default function AdminCalendar({ token, onLogout }) {
  // Les sessions admin vivent en mémoire côté serveur (auth.js) : chaque
  // redéploiement Render les efface, et le jeton en localStorage devient
  // invalide sans que rien ne le signale. On tombait alors sur un
  // « Erreur serveur » trompeur — le backend répond pourtant 401 avec un
  // motif clair. On le détecte, on renvoie proprement vers la connexion.
  const verifierSession = (res) => {
    if (res.status === 401) {
      if (onLogout) onLogout();
      const e = new Error('Session expirée — reconnecte-toi, ta modification n\'a pas été enregistrée.');
      e.sessionExpiree = true;
      throw e;
    }
    return res;
  };
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedResa, setSelectedResa] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editResa, setEditResa] = useState(null);
  const [toast, setToast] = useState('');
  const [view, setView] = useState('month'); // 'month' | 'day'
  // ── Module journée groupe ──
  const [groupBookings, setGroupBookings] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showOptout, setShowOptout] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  // ── Fermetures (blocage des nouvelles résas clients) ──
  const [closures, setClosures] = useState([]);
  // ── Fermeture par période (plusieurs créneaux / plusieurs jours d'un coup) ──
  const [bulkService, setBulkService] = useState('caleche');
  const [bulkSel, setBulkSel] = useState([]);
  const [bulkDebut, setBulkDebut] = useState('');
  const [bulkFin, setBulkFin] = useState('');

  const showToast = (msg) => setToast(msg);

  // ── Chargement des réservations ───────────────────────────────
  const loadReservations = useCallback(async () => {
    setLoading(true);
    try {
      const paddedMonth = String(month + 1).padStart(2, '0');
      const res = await fetch(`${API_BASE}/reservations?month=${year}-${paddedMonth}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error('Erreur serveur');
      const data = await res.json();
      const toutes = Array.isArray(data) ? data : (data.reservations || []);
      setReservations(toutes.filter(r => r.statut !== 'annulee'));
    } catch (e) {
      console.error('Erreur chargement réservations:', e);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadReservations(); }, [loadReservations]);

  // ── Chargement des journées groupe ────────────────────────────
  const loadGroupBookings = useCallback(async () => {
    try {
      const paddedMonth = String(month + 1).padStart(2, '0');
      const res = await fetch(`${API_BASE}/admin/group-bookings?month=${year}-${paddedMonth}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error('Erreur serveur');
      const data = await res.json();
      setGroupBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur chargement journées groupe:', e);
    }
  }, [year, month, token]);

  useEffect(() => { loadGroupBookings(); }, [loadGroupBookings]);

  const getGroupsForDate = (dateStr) =>
    groupBookings.filter(g => formatDate(g.booking_date) === dateStr);

  // ── Fermetures ────────────────────────────────────────────────
  const loadClosures = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/closures`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error('Erreur serveur');
      const data = await res.json();
      setClosures(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur chargement fermetures:', e);
    }
  }, [token]);

  useEffect(() => { loadClosures(); }, [loadClosures]);

  const getClosuresForDate = (dateStr) =>
    closures.filter(c => formatDate(c.date) === dateStr);

  const addClosure = async (service, periode, date, heure) => {
    try {
      const res = await fetch(`${API_BASE}/closures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
        body: JSON.stringify({ service, periode, date, heure: heure || null }),
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
      const quoi = heure ? ` départ de ${heure} fermé` : ` fermé${periode === 'journee' ? ' toute la journée' : ' le ' + periode}`;
      showToast(`⛔ ${service === 'restaurant' ? 'Restaurant' : 'Calèches'} —${quoi}`);
      await loadClosures();
    } catch {
      showToast('❌ Erreur lors de la fermeture');
    }
  };

  // Fermer ('close') ou rouvrir ('open') les créneaux sélectionnés sur la période bulkDebut→bulkFin
  const bulkClosure = async (mode) => {
    const debut = bulkDebut || selectedDate;
    const fin = bulkFin || debut;
    if (!bulkSel.length) { showToast('⚠️ Sélectionne au moins un créneau'); return; }
    if (fin < debut) { showToast('⚠️ La date de fin est avant la date de début'); return; }
    const items = bulkSel.map(k => (k === 'journee' || k === 'midi' || k === 'soir') ? { periode: k } : { heure: k });
    try {
      const res = await fetch(`${API_BASE}/closures/${mode === 'close' ? 'bulk' : 'bulk-delete'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
        body: JSON.stringify({ service: bulkService, date_debut: debut, date_fin: fin, items }),
      });
      const data = await res.json().catch(() => ({}));
      verifierSession(res);
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');
      showToast(mode === 'close' ? `⛔ ${data.created} fermeture(s) créée(s)` : `✅ ${data.deleted} créneau(x) rouvert(s)`);
      await loadClosures();
    } catch (e) {
      showToast('❌ ' + e.message);
    }
  };

  const removeClosure = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/closures/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
      showToast('✅ Réservations rouvertes');
      await loadClosures();
    } catch {
      showToast('❌ Erreur lors de la réouverture');
    }
  };

  // ── CRUD journée groupe ───────────────────────────────────────
  const handleSaveGroup = async (payload) => {
    const isEdit = !!payload.id;
    const url = isEdit ? `${API_BASE}/admin/group-bookings/${payload.id}` : `${API_BASE}/admin/group-bookings`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      body: JSON.stringify(payload),
    });
    verifierSession(res);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Erreur serveur');
    }
    showToast(isEdit ? '✅ Journée groupe modifiée' : '✅ Journée groupe créée');
    await loadGroupBookings();
  };

  const handleDeleteGroup = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/admin/group-bookings/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
      setShowGroupModal(false); setEditGroup(null);
      showToast('🗑️ Journée groupe supprimée');
      await loadGroupBookings();
    } catch (e) {
      showToast(e && e.sessionExpiree ? '🔒 ' + e.message : '❌ Erreur lors de la suppression');
    }
  };

  // ── Navigation ─────────────────────────────────────────────────
  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // ── Resas par jour ────────────────────────────────────────────
  const getResasForDate = (dateStr) =>
    reservations.filter(r => formatDate(r.date) === dateStr)
      .sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));

  // ── CRUD ──────────────────────────────────────────────────────
  const handleSave = async (formData) => {
    const isEdit = !!formData.id;
    const url = isEdit ? `${API_BASE}/reservations/${formData.id}` : `${API_BASE}/reservations`;
    const method = isEdit ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      body: JSON.stringify(formData),
    });
    verifierSession(res);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Créneau fermé : seul le mot de passe "force" permet de passer outre
      if (res.status === 423 && err.ferme_admin && !isEdit) {
        const mdp = window.prompt('⛔ Ce créneau est fermé aux réservations.\n\nMot de passe pour forcer :');
        if (mdp) {
          const res2 = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
            body: JSON.stringify({ ...formData, force_password: mdp }),
          });
          if (res2.ok) {
            showToast('⚡ Réservation forcée sur créneau fermé');
            await loadReservations();
            return;
          }
          const err2 = await res2.json().catch(() => ({}));
          throw new Error(err2.message || err2.error || 'Mot de passe incorrect');
        }
      }
      throw new Error(err.message || err.error || 'Erreur serveur');
    }
    showToast(isEdit ? '✅ Réservation modifiée' : '✅ Réservation créée');
    await loadReservations();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/reservations/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
      setSelectedResa(null);
      showToast('🗑️ Réservation annulée');
      await loadReservations();
    } catch (e) {
      showToast(e && e.sessionExpiree ? '🔒 ' + e.message : '❌ Erreur lors de l\'annulation');
    }
  };

  // ── Confirmation manuelle admin ───────────────────────────────
  // Utilise PATCH /:id/statut avec statut 'confirmee'
  // → déclenche emailValidation + GCal côté backend
  const handleConfirm = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/reservations/${id}/statut`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
        body: JSON.stringify({ statut: 'confirmee' }),
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
      setSelectedResa(null);
      showToast('✅ Réservation confirmée');
      await loadReservations();
    } catch {
      showToast('❌ Erreur lors de la confirmation');
    }
  };

  // ── Toggle arrivee (pointage service) ─────────────────────────
  const handleArrival = async (id, arrived) => {
    setReservations(prev => prev.map(r => r.id === id ? { ...r, arrived } : r));
    try {
      const res = await fetch(`${API_BASE}/reservations/${id}/arrival`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
        body: JSON.stringify({ arrived }),
      });
      verifierSession(res);
      if (!res.ok) throw new Error();
    } catch {
      setReservations(prev => prev.map(r => r.id === id ? { ...r, arrived: !arrived } : r));
      showToast('❌ Erreur pointage');
    }
  };

  // ── Vue Mensuelle ────────────────────────────────────────────
  const today = formatDate(new Date());
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const renderMonthView = () => (
    <div style={styles.calendarGrid}>
      <div style={styles.calendarHeader}>
        {JOURS_FR.map(j => (
          <div key={j} style={styles.calendarHeaderCell}>{j}</div>
        ))}
      </div>
      <div style={styles.calendarBody}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} style={styles.dayCellEmpty} />;
          const paddedMonth = String(month + 1).padStart(2, '0');
          const dateStr = `${year}-${paddedMonth}-${String(day).padStart(2, '0')}`;
          const resas = getResasForDate(dateStr);
          const groups = getGroupsForDate(dateStr);
          const closuresDay = getClosuresForDate(dateStr);
          const isToday = dateStr === today;
          const totalCouverts = resas.reduce((s, r) => s + (r.nb_personnes || 0), 0);

          return (
            <div
              key={day}
              style={{
                ...styles.dayCell,
                ...(isToday ? styles.dayCellToday : {}),
                boxShadow: resas.length > 0 ? '0 2px 8px rgba(74,44,23,0.1)' : 'none',
              }}
              onClick={() => { setSelectedDate(dateStr); setView('day'); }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = COLORS.grisClair; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(74,44,23,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = isToday ? '#FFFBF3' : COLORS.blanc; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = resas.length > 0 ? '0 2px 8px rgba(74,44,23,0.1)' : 'none'; }}
            >
              {isToday ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                  <div style={styles.dayNumberToday}>{day}</div>
                </div>
              ) : (
                <div style={styles.dayNumber}>{day}</div>
              )}

              {closuresDay.length > 0 && (
                <div style={{ position: 'absolute', top: '4px', right: '6px', fontSize: '11px' }} title="Réservations fermées">
                  ⛔{[...new Set(closuresDay.map(c => c.service === 'restaurant' ? '🍽️' : c.service === 'caleche' ? '🐴' : '🔒'))].join('')}
                </div>
              )}
              {groups.map((g, i) => (
                <span
                  key={`g-${g.id}`}
                  onClick={e => { e.stopPropagation(); setEditGroup(g); setShowGroupModal(true); }}
                  style={{
                    ...styles.reservaBadge,
                    backgroundColor: g.status === 'annule' ? '#F2D7D5' : '#FCEFC7',
                    color: g.status === 'annule' ? COLORS.rouge : '#7B5E00',
                    border: `1px solid ${COLORS.or}`,
                    fontWeight: 'bold',
                    textDecoration: g.status === 'annule' ? 'line-through' : 'none',
                  }}
                >
                  🚌 {g.tour_operator} · {g.pax_count}p
                </span>
              ))}
              {resas.slice(0, 3).map((r, i) => {
                const tc = TYPE_CONFIG[r.type] || TYPE_CONFIG.restaurant;
                return (
                  <span key={i} style={{
                    ...styles.reservaBadge,
                    backgroundColor: tc.bg,
                    color: tc.color,
                  }}>
                    {tc.icon} {r.heure} {r.nom}
                  </span>
                );
              })}
              {resas.length > 3 && <div style={styles.moreBadge}>+{resas.length - 3} autres</div>}
              {resas.length > 0 && (
                <div style={{ position: 'absolute', bottom: '4px', right: '6px', fontSize: '10px', color: COLORS.gris, fontWeight: 'bold' }}>
                  👥{totalCouverts}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Vue Journalière ───────────────────────────────────────────
  const renderDayView = () => {
    const resas = selectedDate ? getResasForDate(selectedDate) : [];
    const groupsDay = selectedDate ? getGroupsForDate(selectedDate) : [];
    const totalCouverts = resas.reduce((s, r) => s + (r.nb_personnes || 0), 0);
    const hasRestaurant = resas.some(r => r.type === 'restaurant' || r.type === 'journee_complete' || r.type === 'caleche_fondue');
    const hasCaleche = resas.some(r => ['caleche','caleche_bateau','journee_complete','caleche_fondue'].includes(r.type));

    const resasResto = resas.filter(r => r.type === 'restaurant');
    const resasCaleche = resas.filter(r => r.type !== 'restaurant');

    return (
      <div style={styles.dayView}>
        <div style={styles.dayViewHeader}>
          <div>
            <button
              style={{ ...styles.btnSecondary, marginBottom: '8px', fontSize: '12px', padding: '6px 12px' }}
              onClick={() => setView('month')}
            >
              ← Calendrier
            </button>
            <div style={styles.dayViewTitle}>{selectedDate ? formatDateFR(selectedDate) : ''}</div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => { setEditResa(null); setShowForm(true); }}
          >
            ➕ Nouvelle réservation
          </button>
        </div>

        {/* Stats par activite */}
        <div style={styles.statsBar}>
          {resasResto.length > 0 && (
            <div style={{ ...styles.statCard, borderLeft: '4px solid #8B5E3C' }}>
              <div style={{ fontSize: '22px', marginBottom: '2px' }}>🍽️</div>
              <div style={styles.statNumber}>{resasResto.length}</div>
              <div style={styles.statLabel}>
                {resasResto.length > 1 ? 'résas' : 'résa'} · {resasResto.reduce((s, r) => s + (r.nb_personnes || 0), 0)} couverts
              </div>
            </div>
          )}
          {resasCaleche.length > 0 && (
            <div style={{ ...styles.statCard, borderLeft: '4px solid #D4A017' }}>
              <div style={{ fontSize: '22px', marginBottom: '2px' }}>🐴</div>
              <div style={styles.statNumber}>{resasCaleche.length}</div>
              <div style={styles.statLabel}>
                {resasCaleche.length > 1 ? 'résas' : 'résa'} · {resasCaleche.reduce((s, r) => s + (r.nb_personnes || 0), 0)} pers.
              </div>
            </div>
          )}
          {resas.length === 0 && (
            <div style={styles.statCard}>
              <div style={styles.statNumber}>0</div>
              <div style={styles.statLabel}>Réservation</div>
            </div>
          )}
        </div>

        {/* Fermetures — bloque les nouvelles résas clients (site, Sparrow, Clara) */}
        {(() => {
          const closuresDay = selectedDate ? getClosuresForDate(selectedDate) : [];
          const findC = (service, periode, heure) => closuresDay.find(c => c.service === service && c.periode === periode && (heure ? (c.heure || '').substring(0, 5) === heure : !c.heure));
          const renderBtn = (service, periode, label, heure) => {
            const c = findC(service, periode, heure);
            const journeeFermee = periode !== 'journee' && !!findC(service, 'journee');
            return (
              <button
                key={service + periode + (heure || '')}
                disabled={journeeFermee}
                onClick={() => c ? removeClosure(c.id) : addClosure(service, periode, selectedDate, heure)}
                style={{ padding: '8px 14px', borderRadius: '8px', border: c ? 'none' : `1px solid ${COLORS.grisClair}`, cursor: journeeFermee ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: c ? COLORS.rouge : COLORS.blanc, color: c ? COLORS.blanc : COLORS.brun, opacity: journeeFermee ? 0.35 : 1, transition: 'all .15s' }}
              >
                {c ? `⛔ ${label} — FERMÉ` : label}
              </button>
            );
          };
          const autres = closuresDay.filter(c => !['restaurant', 'caleche'].includes(c.service));
          return (
            <div style={{ backgroundColor: COLORS.blanc, border: `1px solid ${COLORS.grisClair}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                ⛔ Fermer les réservations
              </div>
              <div style={{ fontSize: '12px', color: COLORS.gris, marginBottom: '12px' }}>
                Bloque les nouvelles résas clients (site, Sparrow, Clara) pour ce jour — les résas existantes ne bougent pas. Recliquer pour rouvrir.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <span style={{ minWidth: '120px', fontWeight: 'bold', fontSize: '14px', color: COLORS.brun }}>🍽️ Restaurant</span>
                {renderBtn('restaurant', 'journee', 'Journée')}
                {renderBtn('restaurant', 'midi', 'Midi')}
                {renderBtn('restaurant', 'soir', 'Soir')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ minWidth: '120px', fontWeight: 'bold', fontSize: '14px', color: COLORS.brun }}>🐴 Calèches</span>
                {renderBtn('caleche', 'journee', 'Journée')}
                {CONFIG.DEPARTS_ACTIFS.map(h => renderBtn('caleche', 'creneau', `Départ ${h}`, h))}
              </div>
              {autres.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                  {autres.map(c => (
                    <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#F2D7D5', color: COLORS.rouge, borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: 'bold' }}>
                      🔒 Tout fermé{c.periode !== 'journee' ? ` (${c.periode})` : ''}
                      <button onClick={() => removeClosure(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.rouge, fontSize: '14px', padding: 0 }}>✕</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Fermeture par période : plusieurs créneaux × plusieurs jours d'un coup */}
              {(() => {
                const CRENEAUX_PAR_SERVICE = {
                  caleche: [{ k: 'journee', l: 'Journée' }, ...CONFIG.DEPARTS_ACTIFS.map(h => ({ k: h, l: `Départ ${h}` }))],
                  restaurant: [{ k: 'journee', l: 'Journée' }, { k: 'midi', l: 'Midi' }, { k: 'soir', l: 'Soir' }],
                  all: [{ k: 'journee', l: 'Journée' }],
                };
                const creneaux = CRENEAUX_PAR_SERVICE[bulkService] || [];
                const toggleSel = (k) => setBulkSel(sel => sel.includes(k) ? sel.filter(x => x !== k) : [...sel, k]);
                const inputStyle = { padding: '7px 10px', borderRadius: '8px', border: `1px solid ${COLORS.grisClair}`, fontSize: '13px', color: COLORS.brun, backgroundColor: COLORS.blanc };
                return (
                  <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px dashed ${COLORS.grisClair}` }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                      📆 Sur une période
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <select value={bulkService} onChange={e => { setBulkService(e.target.value); setBulkSel([]); }} style={inputStyle}>
                        <option value="caleche">🐴 Calèches</option>
                        <option value="restaurant">🍽️ Restaurant</option>
                        <option value="all">🔒 Tout</option>
                      </select>
                      {creneaux.map(c => (
                        <button key={c.k} onClick={() => toggleSel(c.k)}
                          style={{ padding: '7px 12px', borderRadius: '8px', border: bulkSel.includes(c.k) ? 'none' : `1px solid ${COLORS.grisClair}`, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: bulkSel.includes(c.k) ? COLORS.brun : COLORS.blanc, color: bulkSel.includes(c.k) ? COLORS.or : COLORS.brun, transition: 'all .15s' }}>
                          {bulkSel.includes(c.k) ? '✓ ' : ''}{c.l}
                        </button>
                      ))}
                      <span style={{ fontSize: '13px', color: COLORS.gris }}>du</span>
                      <input type="date" value={bulkDebut || selectedDate || ''} onChange={e => setBulkDebut(e.target.value)} style={inputStyle} />
                      <span style={{ fontSize: '13px', color: COLORS.gris }}>au</span>
                      <input type="date" value={bulkFin || bulkDebut || selectedDate || ''} onChange={e => setBulkFin(e.target.value)} style={inputStyle} />
                      <button onClick={() => bulkClosure('close')} style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: COLORS.rouge, color: COLORS.blanc }}>
                        ⛔ Fermer
                      </button>
                      <button onClick={() => bulkClosure('open')} style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: COLORS.vert, color: COLORS.blanc }}>
                        ✅ Rouvrir
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Journées groupe du jour */}
        {groupsDay.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', padding: '8px 0 6px', borderBottom: `2px solid ${COLORS.or}`, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🚌 Journées groupe <span style={{ fontSize: '11px', fontWeight: 'normal', color: COLORS.gris }}>({groupsDay.length})</span>
            </div>
            {groupsDay.map(g => (
              <div key={g.id} style={{ ...styles.reservaCard, borderLeft: `4px solid ${COLORS.or}` }}
                onClick={() => { setEditGroup(g); setShowGroupModal(true); }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = COLORS.grisClair; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = COLORS.blanc; }}
              >
                <div style={{ fontSize: '22px', minWidth: '40px', textAlign: 'center' }}>🚌</div>
                <div style={styles.reservaInfo}>
                  <div style={styles.reservaName}>
                    {g.tour_operator}
                    {g.status === 'annule' && <span style={{ marginLeft: '8px', fontSize: '11px', color: COLORS.rouge }}>(annulé)</span>}
                  </div>
                  <div style={styles.reservaDetails}>
                    👥 {g.pax_count} pax · {(g.segments || []).length} activité{(g.segments || []).length > 1 ? 's' : ''}
                    {g.nb_groups > 1 && ` · ${g.nb_groups} sous-groupes`}
                    {g.contact_name && ` · ${g.contact_name}`}
                    {g.email_thread_count > 0 && (
                      <span title={`${g.email_thread_count} fil${g.email_thread_count > 1 ? 's' : ''} de mails connu${g.email_thread_count > 1 ? 's' : ''}`}> · ✉️ {g.email_thread_count}</span>
                    )}
                  </div>
                </div>
                <span style={{ ...styles.typePill, backgroundColor: g.status === 'confirme' ? '#D5F5E3' : '#FCEFC7', color: g.status === 'confirme' ? COLORS.vert : '#7B5E00' }}>
                  {GROUP_STATUS_LABEL[g.status] || g.status}
                </span>
                <div style={{ color: COLORS.gris, fontSize: '18px' }}>›</div>
              </div>
            ))}
          </div>
        )}

        {/* Liste des resas */}
        {resas.length === 0 ? (
          <div style={styles.emptyDay}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
            Aucune réservation ce jour
            <br />
            <button
              style={{ ...styles.btnPrimary, margin: '20px auto', display: 'inline-flex' }}
              onClick={() => { setEditResa(null); setShowForm(true); }}
            >
              ➕ Ajouter une réservation
            </button>
          </div>
        ) : (
          (() => {
            const resasMidi = resas.filter(r => !r.heure || r.heure < '17:00');
            const resasSoir = resas.filter(r => r.heure && r.heure >= '17:00');
            const renderCard = (r) => {
              const tc = TYPE_CONFIG[r.type] || TYPE_CONFIG.restaurant;
              return (
                <div key={r.id} style={styles.reservaCard}
                  onClick={() => setSelectedResa(r)}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = COLORS.grisClair; e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.borderLeft = `4px solid ${COLORS.or}`; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = COLORS.blanc; e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderLeft = `1px solid ${COLORS.grisClair}`; }}
                >
                  <div style={styles.reservaTime}>
                    {r.type === 'journee_complete' && (!r.heure || r.heure === '10:00') ? '11:30' : r.heure}
                  </div>
                  <div style={styles.reservaInfo}>
                    <div style={styles.reservaName}>{r.nom}</div>
                    <div style={styles.reservaDetails}>
                      👥 {r.nb_personnes} pers.
                      {r.telephone && ` · 📞 ${r.telephone}`}
                      {r.commentaires && ` · 📝 ${r.commentaires.substring(0, 40)}${r.commentaires.length > 40 ? '...' : ''}`}
                    </div>
                  </div>
                  <div>
                    <span style={{ ...styles.typePill, backgroundColor: tc.bg, color: tc.color }}>
                      {tc.icon} {tc.label}
                    </span>
                    {r.statut === 'confirmee' && (
                      <div style={{ fontSize: '10px', color: COLORS.vert, textAlign: 'center', marginTop: '4px', fontWeight: 'bold' }}>✅ confirmée</div>
                    )}
                    {r.source === 'admin' && r.statut !== 'confirmee' && (
                      <div style={{ fontSize: '10px', color: COLORS.gris, textAlign: 'center', marginTop: '4px' }}>manuel</div>
                    )}
                  </div>
                  {r.statut === 'confirmee' && (
                    <label
                      onClick={e => e.stopPropagation()}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', background: r.arrived ? '#059669' : '#f1f5f9', color: r.arrived ? 'white' : '#475569', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', userSelect: 'none', transition: 'all .15s', marginRight: '8px' }}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.arrived}
                        onChange={e => { e.stopPropagation(); handleArrival(r.id, e.target.checked); }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', margin: 0 }}
                      />
                      {r.arrived ? '✅ Arrivé' : 'Arrivé ?'}
                    </label>
                  )}
                  <div style={{ color: COLORS.gris, fontSize: '18px' }}>›</div>
                </div>
              );
            };
            return (
              <>
                {resasMidi.length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', padding: '8px 0 6px', borderBottom: `2px solid ${COLORS.or}`, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🌞 Service du Midi <span style={{ fontSize: '11px', fontWeight: 'normal', color: COLORS.gris }}>({resasMidi.reduce((s, r) => s + (r.nb_personnes || 0), 0)} couverts)</span>
                    </div>
                    {resasMidi.map(renderCard)}
                  </div>
                )}
                {resasSoir.length > 0 && (
                  <div style={{ marginTop: resasMidi.length > 0 ? '20px' : '0' }}>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', padding: '8px 0 6px', borderBottom: `2px solid ${COLORS.bleu}`, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🌙 Service du Soir <span style={{ fontSize: '11px', fontWeight: 'normal', color: COLORS.gris }}>({resasSoir.reduce((s, r) => s + (r.nb_personnes || 0), 0)} couverts)</span>
                    </div>
                    {resasSoir.map(renderCard)}
                  </div>
                )}
              </>
            );
          })()
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: ${COLORS.or} !important; box-shadow: 0 0 0 3px rgba(232,196,124,0.2); }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>🗓️ Calendrier Admin — Surabaya Café</div>
          <div style={styles.headerSub}>Gestion des réservations</div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btnPrimary, backgroundColor: 'transparent', color: COLORS.or, border: `2px solid ${COLORS.or}` }}
            onClick={() => { setEditGroup(null); setShowGroupModal(true); }}
          >
            🚌 Journée groupe
          </button>
          <button
            style={{ ...styles.btnPrimary, backgroundColor: 'transparent', color: COLORS.or, border: `2px solid ${COLORS.or}` }}
            onClick={() => setShowOptout(true)}
            title="Qui ne reçoit plus les campagnes email"
          >
            ✉️ Désinscriptions
          </button>
          <button
            style={styles.btnPrimary}
            onClick={() => {
              setEditResa(null);
              setSelectedDate(view === 'day' ? selectedDate : today);
              setShowForm(true);
            }}
          >
            ➕ Nouvelle résa
          </button>
        </div>
      </div>

      {/* Navigation mois */}
      <div style={styles.navBar}>
        <button style={styles.navBtn} onClick={prevMonth}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.monthTitle}>{MOIS_FR[month]} {year}</div>
          {loading && <div style={{ fontSize: '11px', color: COLORS.gris }}>Chargement...</div>}
        </div>
        <button style={styles.navBtn} onClick={nextMonth}>›</button>
      </div>

      {/* Vue */}
      {view === 'month' ? renderMonthView() : renderDayView()}

      {/* Modal détail */}
      {selectedResa && (
        <ReservationDetailModal
          reservation={selectedResa}
          onClose={() => setSelectedResa(null)}
          onDelete={handleDelete}
          onEdit={(r) => { setSelectedResa(null); setEditResa(r); setShowForm(true); }}
          onConfirm={handleConfirm}
        />
      )}

      {/* Modal formulaire */}
      {showForm && (
        <ReservationFormModal
          onClose={() => { setShowForm(false); setEditResa(null); }}
          onSave={handleSave}
          initialData={editResa}
          initialDate={selectedDate || today}
        />
      )}

      {/* Modal journée groupe */}
      {showGroupModal && (
        <GroupBookingModal
          token={token}
          onClose={() => { setShowGroupModal(false); setEditGroup(null); }}
          onSave={handleSaveGroup}
          onDelete={handleDeleteGroup}
          initialData={editGroup}
          initialDate={selectedDate || today}
        />
      )}

      {/* Modal désinscriptions marketing */}
      {showOptout && (
        <DesinscriptionsModal token={token} onClose={() => setShowOptout(false)} />
      )}

      {/* Toast */}
      {toast && <Toast message={toast} onClose={() => setToast('')} />}
    </div>
  );
}
